// React
import { useState, useCallback, useEffect } from 'react'

// Components
import {
  UserApproveRegistrationDialog,
  UserBulkRegisterDialog,
  UserDetailDialog,
  UserRejectRegistrationDialog,
  UserTable,
} from 'pages/back-office/user/components'
import { BaseDialogVerification } from 'components'

// Custom Hook
import { useRequestSaver, useUser, useMasterRole, useMasterCompany, usePagination, useAuth } from 'hooks'

// Mobx
import { observer } from 'mobx-react'

// Interfaces
import { IUserWrapperProps, TUserDetailDialog, USER_DETAIL_DIALOG_TYPE } from './interfaces'
import { TUserComponentsDialogType } from 'pages/back-office/user/components/interfaces'
import { IUserUpdateForm } from 'interfaces/back-office/user'
import { ITableEventAttrs } from 'interfaces/table'

// Constant
import { REQUEST } from 'constant/request.constant'
import { USER_STATUS } from 'constant/shared.constant'

// i18n
import { useTranslation } from 'react-i18next'

// Utils
import { messageUtils_success, messageUtils_error } from 'utils/common/message.utils'

// Antd
import { Button } from 'antd'

const UserWrapper = observer(({ status }: IUserWrapperProps) => {
  // Hook
  const { pagination, pagination_onChange } = usePagination({})
  const {
    user_loading,
    user_list,
    user_detail,
    user_rejectForm,
    user_approveForm,
    user_SET_REJECT_FORM,
    user_SET_APPROVE_FORM,
    user_CLEAR_LIST,
    user_CLEAR_DETAIL,
    user_CLEAR_REJECT_FORM,
    user_CLEAR_APPROVE_FORM,
    user_fetchList,
    user_fetchDetail,
    user_create,
    user_update,
    user_approve,
    user_reject,
    user_delete,
  } = useUser()
  const { masterRole_loading, masterRole_dropdown, masterRole_CLEAR_DROPDOWN, masterRole_fetchDropdown } = useMasterRole()
  const { masterCompany_CLEAR_DETAIL, masterCompany_CLEAR_DROPDOWN } = useMasterCompany()
  const [dialog, setDialog] = useState<{
    isBulkRegisterDialogOpen: boolean
    isDetailDialogOpen: boolean
    isApproveDialogOpen: boolean
    isRejectDialogOpen: boolean
    isConfirmationDialogOpen: boolean
  }>({
    isBulkRegisterDialogOpen: false,
    isDetailDialogOpen: false,
    isApproveDialogOpen: false,
    isRejectDialogOpen: false,
    isConfirmationDialogOpen: false,
  })
  const [detailDialogEditableType, setDetailDialogEditableType] = useState<TUserDetailDialog>('')
  const { requestSaver_setCancelToken, requestSaver_cancel } = useRequestSaver()
  const { t } = useTranslation()
  const { auth_persisted, auth_getAuthenticatedUser } = useAuth()

  // Do when user first came to the component
  useEffect(() => {
    getUserList()

    // eslint-disable-next-line
  }, [pagination])

  // Sort by created date 
  useEffect(() => {
    onChangeTable({ name: 'sortColumn', value: 'createdOn' })

    // eslint-disable-next-line
  }, [user_list])

  // Do when user leaving the component
  useEffect(() => {
    return () => {
      onClear()
    }

    // eslint-disable-next-line
  }, [])

  /**
   * @description Clear state from current component
   *
   * @return {void} void
   */
  const onClear = useCallback((): void => {
    user_CLEAR_LIST()

    // Clear additional data
    onClearAdditionalData()

    // eslint-disable-next-line
  }, [])

  /**
   * @description Clear additional data
   *
   * @return {void} void
   */
  const onClearAdditionalData = useCallback((): void => {
    user_CLEAR_DETAIL()
    masterRole_CLEAR_DROPDOWN()
    user_CLEAR_REJECT_FORM()
    user_CLEAR_APPROVE_FORM()
    masterCompany_CLEAR_DETAIL()
    masterCompany_CLEAR_DROPDOWN()
    setDetailDialogEditableType('')

    // eslint-disable-next-line
  }, [])

  /**
   * @description Get users
   *
   * @return {Promise<void>} Promise<void>
   */
  const getUserList = useCallback((): Promise<void> => {
    return user_fetchList({
      body: { ...pagination, status },
      requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_USER_MANAGEMENT_FETCH_LIST_REQUEST, status) },
    })
  }, [user_fetchList, pagination, requestSaver_setCancelToken, status])

  /**
   * @description Get single user
   *
   * @param {number} id
   *
   * @return {Promise<void>} Promise<void>
   */
  const getUserDetail = useCallback(
    (id: number): Promise<void> => {
      return user_fetchDetail({
        params: { id },
        requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_USER_MANAGEMENT_FETCH_DETAIL_REQUEST) },
      })
    },
    [user_fetchDetail, requestSaver_setCancelToken],
  )

  /**
   * @description Get role dropdown
   *
   * @return {Promise<void>} Promise<void>
   */
  const getRoleDropdown = useCallback((): Promise<void> => {
    return masterRole_fetchDropdown({
      requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_MASTER_DATA_ROLE_FETCH_DROPDOWN_REQUEST) },
    })

    // eslint-disable-next-line
  }, [])

  /**
   * @description Create users
   *
   * @param {IUserUpdateForm[]} forms
   *
   * @return {Promise<void>} Promise<void>
   */
  const createUsers = useCallback(
    (forms: IUserUpdateForm[]): Promise<void> => {
      return user_create({
        body: { users: forms },
        requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_USER_MANAGEMENT_CREATE_REQUEST) },
      })
    },
    [user_create, requestSaver_setCancelToken],
  )

  /**
   * @description Approve user
   *
   * @param {number} id
   * @param {{roleIds: number[]}} form
   *
   * @return {Promise<void>} Promise<void>
   */
  const approveUser = useCallback(
    (id: number, form: { roleIds: number[] }): Promise<void> => {
      return user_approve({
        params: { id },
        body: { roleIds: form.roleIds },
        requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_USER_MANAGEMENT_APPROVE_REQUEST) },
      })
    },
    [user_approve, requestSaver_setCancelToken],
  )

  /**
   * @description Reject user
   *
   * @param {number} id
   * @param {{reason: string}} form
   *
   * @return {Promise<void>} Promise<void>
   */
  const rejectUser = useCallback(
    (id: number, form: { reason: string }): Promise<void> => {
      return user_reject({
        params: { id },
        body: { reason: form.reason },
        requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_USER_MANAGEMENT_REJECT_REQUEST) },
      })
    },
    [user_reject, requestSaver_setCancelToken],
  )

  /**
   * @description Remove user
   *
   * @param {number} id
   *
   * @return {Promise<void>} Promise<void>
   */
  const deleteUser = useCallback(
    (id: number): Promise<void> => {
      return user_delete({
        params: { id },
        requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_USER_MANAGEMENT_REMOVE_REQUEST) },
      })
    },
    [user_delete, requestSaver_setCancelToken],
  )

  /**
   * @description Change Account Status 
   *
   * @param {number} userId
   * @param {IUserUpdateForm} form
   *
   * @return {Promise<void>} Promise<void>
   */
  const changeAccountStatus = useCallback(
    (userId: number, accountStatus: boolean): Promise<void> => {
      return user_update({
        params: { id: userId },
        body: { 
          name: user_detail.name,
          email: user_detail.email,
          npk: user_detail.npk,
          companyId: user_detail.companyId,
          fieldId: user_detail.fieldId,
          roleIds: user_detail.roles.map(role => role.id!),
          accountStatus
        },
        requestConfig: { ...requestSaver_setCancelToken(REQUEST.BACK_OFFICE_USER_MANAGEMENT_UPDATE_REQUEST) },
      })
    },
    [requestSaver_setCancelToken, user_update, user_detail],
  )

  /**
   * @description Handle dialog
   *
   * @param {TUserComponentsDialogType} dialogType
   * @param {boolean} value
   * @param {object} options
   *
   * @return {Promise<void>} Promise<void>
   */
  const handleDialog = useCallback(
    async (
      dialogType: TUserComponentsDialogType,
      value: boolean,
      options?: { editableDialogType?: TUserDetailDialog; userId?: number },
    ): Promise<void> => {
      setDialog(dialog => ({
        ...dialog,
        [dialogType]: value,
      }))

      // Load user, otherwise, close the modal
      try {
        if (value) {
          // Check for user ID
          if (options?.userId) await getUserDetail(options.userId)

          // Check for editable dialog type
          if (options?.editableDialogType) setDetailDialogEditableType(options.editableDialogType)

          // Check if opening approval
          if (dialogType === 'isApproveDialogOpen') await getRoleDropdown()
        }
      } catch (err) {
        handleDialog(dialogType, false)
      }

      if (!value) {
        // Don't clear state when one of this dialogs opened
        const whitelistedDialogs = ['isRejectDialogOpen', 'isApproveDialogOpen'].includes(dialogType)
        const isWaitingApprovalAndOpeningDetail =
          user_detail.status === USER_STATUS.WAITING_APPROVAL && dialogType === 'isDetailDialogOpen'

        if (!whitelistedDialogs && !isWaitingApprovalAndOpeningDetail) {
          // Clear additional data
          onClearAdditionalData()
        }

        // Cancel request if theres any request in the background
        requestSaver_cancel(REQUEST.BACK_OFFICE_USER_MANAGEMENT_FETCH_DETAIL_REQUEST)
        requestSaver_cancel(REQUEST.BACK_OFFICE_MASTER_DATA_ROLE_FETCH_DROPDOWN_REQUEST)
        requestSaver_cancel(REQUEST.BACK_OFFICE_USER_MANAGEMENT_CREATE_REQUEST)
        requestSaver_cancel(REQUEST.BACK_OFFICE_USER_MANAGEMENT_APPROVE_REQUEST)
        requestSaver_cancel(REQUEST.BACK_OFFICE_USER_MANAGEMENT_REJECT_REQUEST)
        requestSaver_cancel(REQUEST.BACK_OFFICE_USER_MANAGEMENT_REMOVE_REQUEST)
        requestSaver_cancel(REQUEST.BACK_OFFICE_USER_MANAGEMENT_UPDATE_REQUEST)
        requestSaver_cancel(REQUEST.BACK_OFFICE_MASTER_DATA_COMPANY_FETCH_DROPDOWN_REQUEST)
        requestSaver_cancel(REQUEST.BACK_OFFICE_MASTER_DATA_COMPANY_FETCH_DETAIL_REQUEST)
      }
    },
    [getRoleDropdown, getUserDetail, onClearAdditionalData, requestSaver_cancel, user_detail.status],
  )

  /**
   * @description Handle user detail dialog click first button
   *
   * @param {IUserUpdateForm} form
   *
   * @return {Promise<void>} Promise<void>
   */
  const handleUserDetailClickFirstButton = useCallback(
    async (form?: IUserUpdateForm): Promise<void> => {
      // Check if handler come from waiting approval user
      if (detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.SEE_USER) {
        handleDialog('isDetailDialogOpen', false)

        handleDialog('isRejectDialogOpen', true, { editableDialogType: USER_DETAIL_DIALOG_TYPE.REJECT_USER })
      }

    },
    [detailDialogEditableType, handleDialog],
  )

  /**
   * @description Handle user detail dialog click second button
   *
   * @return {void} void
   */
  const handleUserDetailClickSecondButton = useCallback((): void => {
    // Check if handler come from waiting approval user
    if (detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.SEE_USER) {
      handleDialog('isDetailDialogOpen', false)

      handleDialog('isApproveDialogOpen', true, { editableDialogType: USER_DETAIL_DIALOG_TYPE.APPROVE_USER })
    }
  }, [detailDialogEditableType, handleDialog])

  /**
   * @description Handle user approve dialog click first button
   *
   * @param {{roleIds: number[]}} form
   *
   * @return {void} void
   */
  const handleUserApproveClickFirstButton = useCallback(
    (form: { roleIds: number[] }): void => {
      handleDialog('isApproveDialogOpen', false)

      // Set form globally
      user_SET_APPROVE_FORM(form)

      handleDialog('isConfirmationDialogOpen', true, { editableDialogType: USER_DETAIL_DIALOG_TYPE.APPROVE_USER })
    },
    [handleDialog, user_SET_APPROVE_FORM],
  )

  /**
   * @description Handle user reject dialog click first button
   *
   * @param {{reason: string}} form
   *
   * @return {void} void
   */
  const handleUserRejectClickFirstButton = useCallback(
    (form: { reason: string }): void => {
      handleDialog('isRejectDialogOpen', false)

      // Set form globally
      user_SET_REJECT_FORM(form)

      handleDialog('isConfirmationDialogOpen', true, { editableDialogType: USER_DETAIL_DIALOG_TYPE.REJECT_USER })
    },
    [handleDialog, user_SET_REJECT_FORM],
  )

  /**
   * @description Handle verification dialog click first button
   *
   * @return {Promise<void>} Promise<void>
   */
  const handleVerificationClickFirstButton = useCallback(async (): Promise<void> => {
    // Check if user_detail.id is empty
    if (!user_detail?.id) {
      handleDialog('isConfirmationDialogOpen', false)

      messageUtils_error(t(`user.alert.userIdEmpty`))

      return
    }

    const locale =
      detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.APPROVE_USER
        ? 'approve'
        : detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.REJECT_USER
        ? 'reject'
        : 'remove'

    try {
      await (detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.APPROVE_USER
        ? approveUser(user_detail.id, { roleIds: user_approveForm.roleIds })
        : detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.REJECT_USER
        ? rejectUser(user_detail.id, { reason: user_rejectForm.reason })
        : detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.REMOVE_USER
        ? deleteUser(user_detail.id)
        : changeAccountStatus(user_detail.id, !user_detail.accountStatus))

      // Close dialog when success
      handleDialog('isConfirmationDialogOpen', false)

      // Throw success alert
      if (detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.CHANGE_ACCOUNT_STATUS) {
        messageUtils_success(t('user.update.alert.success'))
      }
      else {
        messageUtils_success(t(`user.${locale}.alert.success`))
      }

      // Re-fetch data
      await getUserList()

      // Check if user updating the same user as the logged in
      if (user_detail.id === auth_persisted?.auth_authenticatedUser?.id) {
        await auth_getAuthenticatedUser()
      }
    } catch (err) {
      //
    }
  }, [
    detailDialogEditableType,
    user_detail?.id,
    user_approveForm.roleIds,
    user_rejectForm.reason,
    user_detail?.accountStatus,
    t,
    approveUser,
    auth_getAuthenticatedUser,
    auth_persisted?.auth_authenticatedUser?.id,
    deleteUser,
    getUserList,
    handleDialog,
    rejectUser,
    changeAccountStatus
  ])

  /**
   * @description Watch any change in table
   *
   * @param {ITableEventAttrs} event
   *
   * @return {void} void
   */
  const onChangeTable = useCallback(
    ({ name, value }: ITableEventAttrs): void => {
      pagination_onChange({ name, value })
    },
    [pagination_onChange],
  )

  /**
   * @description Handle bulk insert user
   *
   * @param {IUserUpdateForm[]} forms
   *
   * @return {Promise<void>} Promise<void>
   */
  const handleUserBulkInsertClickFirstButton = useCallback(
    async (forms: IUserUpdateForm[]): Promise<void> => {
      try {
        await createUsers(forms)

        handleDialog('isBulkRegisterDialogOpen', false)

        onChangeTable({ name: 'reset', value: '' })

        messageUtils_success(t('user.bulkRegister.alert.success'))
      } catch (err) {
        //
      }
    },
    [t, createUsers, handleDialog, onChangeTable],
  )

  /**
   * @description Render dialog verification body
   *
   * @return {JSX.Element} JSX.Element
   */
  const renderDialogVerificationBody = useCallback((): JSX.Element => {
    let element

    // Check if user want to approve
    if (detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.APPROVE_USER) {
      element = <p className="text-grey-font text-16">{t('user.approve.confirm')}</p>
    }

    // Check if status is waiting approval
    if (detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.REJECT_USER) {
      element = <p className="text-grey-font text-16">{t('user.reject.confirm')}</p>
    }

    // Check if status is rejected
    if (detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.REMOVE_USER) {
      element = <p className="text-grey-font text-16">{t('user.remove.confirm')}</p>
    }

    // Check if status is change account status
    if (detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.CHANGE_ACCOUNT_STATUS) {
      element = <p className="text-grey-font text-16">{ user_detail.accountStatus ? t('user.update.confirm.deActivate') : t('user.update.confirm.activate')}</p>
    }

    return (
      <div className="flex flex-col items-center justify-center text-center my-8" style={{ width: '100%' }}>
        {element}
        <strong>{user_detail?.name ? `${user_detail?.name}?` : '-'}</strong>
      </div>
    )
  }, [detailDialogEditableType, user_detail?.name, user_detail?.accountStatus, t])

  /**
   * @description Render dialog verification footer for change account status
   *
   * @return {JSX.Element} JSX.Element
   */
  const renderDialogVerificationFooter = useCallback((): JSX.Element => {
    if (detailDialogEditableType !== USER_DETAIL_DIALOG_TYPE.CHANGE_ACCOUNT_STATUS) return <></>
    return <>
      <Button 
        type="primary" 
        className="custom-button" 
        onClick={handleVerificationClickFirstButton} 
        loading={user_loading.isDetailLoading || user_loading.isActionLoading}
        style={{ backgroundColor: user_detail.accountStatus ? '#FF5858' : '#0E38B7' }}
        >
        {t('app.sure')}
      </Button>

      <Button 
        type="text" 
        className="custom-button" 
        onClick={() => handleDialog('isConfirmationDialogOpen', false)} 
        style={{ backgroundColor: user_detail.accountStatus ? '#0E38B7' : '#FF5858' }}
        >
        {t('app.no')}
      </Button>
    </>
  }, [detailDialogEditableType, user_loading, user_detail.accountStatus, t, handleVerificationClickFirstButton, handleDialog])

  return (
    <>
      {/* User - Table */}
      <UserTable status={status} data={user_list} loading={user_loading} onChange={onChangeTable} handleDialog={handleDialog} />

      {/* User - Bulk Insert Dialog */}
      <UserBulkRegisterDialog
        isOpen={dialog.isBulkRegisterDialogOpen}
        loading={user_loading}
        onClickFirstButton={handleUserBulkInsertClickFirstButton}
        onClickCloseButton={() => handleDialog('isBulkRegisterDialogOpen', false)}
      />

      {/* User - Detail Dialog */}
      <UserDetailDialog
        isOpen={dialog.isDetailDialogOpen}
        isEditable={detailDialogEditableType === USER_DETAIL_DIALOG_TYPE.CHANGE_USER_DATA_DETAIL}
        loading={user_loading}
        attrs={{
          user: user_detail,
          isUserActionLoading: user_loading.isActionLoading,
          closeModalCallback: () => handleDialog('isDetailDialogOpen', false),
        }}
        onClickFirstButton={handleUserDetailClickFirstButton}
        onClickSecondButton={handleUserDetailClickSecondButton}
        onClickCloseButton={() => handleDialog('isDetailDialogOpen', false)}
      />

      {/* User - Approve Registration Dialog */}
      <UserApproveRegistrationDialog
        isOpen={dialog.isApproveDialogOpen}
        loading={user_loading.isDetailLoading || masterRole_loading.isDropdownLoading}
        onClickFirstButton={handleUserApproveClickFirstButton}
        onClickCloseButton={() => handleDialog('isApproveDialogOpen', false)}
        attrs={{
          isRoleLoading: masterRole_loading.isDropdownLoading,
          roleDropdown: masterRole_dropdown,
          isUserActionLoading: user_loading.isActionLoading,
        }}
      />

      {/* User - Reject Registration Dialog */}
      <UserRejectRegistrationDialog
        isOpen={dialog.isRejectDialogOpen}
        loading={user_loading.isDetailLoading}
        onClickFirstButton={handleUserRejectClickFirstButton}
        onClickCloseButton={() => handleDialog('isRejectDialogOpen', false)}
        attrs={{
          isUserActionLoading: user_loading.isActionLoading,
        }}
      />

      {/* User - Confirmation When Reject / Delete  */}
      <BaseDialogVerification
        isOpen={dialog.isConfirmationDialogOpen}
        body={renderDialogVerificationBody()}
        firstButtonLabel="app.sure"
        secondButtonLabel="app.no"
        onFirstButtonClick={() => handleVerificationClickFirstButton()}
        onSecondButtonClick={() => handleDialog('isConfirmationDialogOpen', false)}
        onButtonCloseClick={() => handleDialog('isConfirmationDialogOpen', false)}
        isFirstButtonLoading={user_loading.isDetailLoading || user_loading.isActionLoading}
        isUsingSecondButton={!user_loading.isDetailLoading}
        isHorizontalFooter
        isUsingDefaultFooter={detailDialogEditableType !== USER_DETAIL_DIALOG_TYPE.CHANGE_ACCOUNT_STATUS}
        footer={renderDialogVerificationFooter()}
        closable
      />
    </>
  )
})

export { UserWrapper }
